import {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Download, Printer } from "lucide-react"
import {
  apiClient,
  type OrchardAnalyticsRecord,
  type OrchardBlockCreateRequest,
  type OrchardBlockRecord,
  type OrchardCreateRequest,
  type OrchardDetailRecord,
  type OrchardPortfolioAnalyticsRecord,
  type OrchardRecord,
  type OrchardRowCreateRequest,
  type OrchardRowRecord,
  type OrchardTreeCreateRequest,
  type OrchardTreeImportSummary,
  type OrchardTreeRecord,
  type OperatorOrganizationRecord,
} from "@/lib/api/client"
import {
  canAccessOrchardWorkspace,
  canManagePortalOrganizations,
} from "@/features/operator/shared/operator-access"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
  OperatorWorkspaceLayout,
} from "@/features/operator/shared/operator-shell"
import {
  useOperatorShellSession,
} from "@/features/operator/shared/operator-shell-session-context"

const INPUT_CLASS =
  "w-full rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:border-primary/50"

const ORCHARD_SECTION_OPTIONS = [
  {
    description: "Orchard portfolio, scope, and seasonal status.",
    label: "Overview",
    value: "overview",
  },
  {
    description: "Create orchards and load orchard detail.",
    label: "Orchards",
    value: "orchards",
  },
  {
    description: "Build orchard structure with blocks, rows, and trees.",
    label: "Trees",
    value: "trees",
  },
  {
    description: "Prepare the orchard registry for QR export and printing.",
    label: "QR Export",
    value: "qr_export",
  },
] as const

type OrchardSection = (typeof ORCHARD_SECTION_OPTIONS)[number]["value"]

export interface OrchardWorkspaceSearch {
  analyticsFrom?: string
  analyticsTo?: string
  organizationId?: string
  orchardId?: string
  section?: OrchardSection
}

interface OrchardWorkspaceProps {
  onSearchChange: (next: Partial<OrchardWorkspaceSearch>) => void
  search: OrchardWorkspaceSearch
}

interface OrchardWorkspaceBundle {
  organizations: OperatorOrganizationRecord[]
  orchards: OrchardRecord[]
}

declare global {
  interface Window {
    QRCode?: QRCodeLibrary
  }
}

type QRCodeInstance = {
  clear?: () => void
}

type QRCodeConstructor = new (
  element: HTMLElement,
  options: {
    colorDark?: string
    colorLight?: string
    correctLevel?: unknown
    height?: number
    text: string
    width?: number
  }
) => QRCodeInstance

type QRCodeLibrary = QRCodeConstructor & {
  CorrectLevel?: {
    M?: unknown
  }
}

interface OrchardBlockFormState {
  code: string
  name: string
  sequence: string
  varietyOverride: string
}

interface OrchardRowFormState {
  blockId: string
  code: string
  orientation: string
  sequence: string
}

interface OrchardTreeFormState {
  ageYears: string
  blockId: string
  plantingYear: string
  qrToken: string
  rowId: string
  status: string
  treeCode: string
  variety: string
}

interface OrchardCreateFormState {
  areaAcres: string
  cropType: string
  district: string
  managerName: string
  managerPhone: string
  name: string
  ownerName: string
  plantingYear: string
  primaryVariety: string
  state: string
  taluk: string
  village: string
}

const INITIAL_CREATE_FORM: OrchardCreateFormState = {
  areaAcres: "",
  cropType: "mango",
  district: "",
  managerName: "",
  managerPhone: "",
  name: "",
  ownerName: "",
  plantingYear: "",
  primaryVariety: "",
  state: "Karnataka",
  taluk: "",
  village: "",
}

const INITIAL_BLOCK_FORM: OrchardBlockFormState = {
  code: "",
  name: "",
  sequence: "",
  varietyOverride: "",
}

const INITIAL_ROW_FORM: OrchardRowFormState = {
  blockId: "",
  code: "",
  orientation: "",
  sequence: "",
}

const INITIAL_TREE_FORM: OrchardTreeFormState = {
  ageYears: "",
  blockId: "",
  plantingYear: "",
  qrToken: "",
  rowId: "",
  status: "active",
  treeCode: "",
  variety: "",
}

const ORCHARD_TREE_IMPORT_TEMPLATE = [
  "block_code,row_code,tree_code,variety,status,planting_year,age_years,qr_token",
  "A,R1,TREE-101,Alphonso,active,2021,5,",
  "A,R1,TREE-102,Alphonso,active,2021,5,",
].join("\n")

let qrCodeLibraryPromise: Promise<void> | null = null

function getSectionValue(section: OrchardWorkspaceSearch["section"]): OrchardSection {
  return ORCHARD_SECTION_OPTIONS.some((option) => option.value === section)
    ? (section as OrchardSection)
    : "overview"
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "0"
  }

  return new Intl.NumberFormat("en-IN").format(value)
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-IN", {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  })
}

function formatObservationTypeLabel(value: string) {
  switch (value) {
    case "flowering":
      return "Flowering"
    case "fruit_set":
      return "Fruit set"
    case "fruit_load":
      return "Fruit load"
    case "harvest":
      return "Harvest"
    case "health_check":
      return "Health check"
    default:
      return value
  }
}

function normalizeAnalyticsDateValue(value: string | undefined) {
  if (!value) {
    return undefined
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined
}

function formatPercentage(value: number) {
  return `${Math.round(value * 100)}%`
}

function formatSignedDelta(value: number) {
  if (!value) {
    return "0"
  }

  return `${value > 0 ? "+" : ""}${value}`
}

function formatBlockAnomalyReason(reason: string) {
  switch (reason) {
    case "activity_spike":
      return "Activity spike"
    case "low_coverage":
      return "Low coverage"
    case "yield_below_baseline":
      return "Yield below baseline"
    default:
      return reason
  }
}

function encodeCsvCell(value: string | number | null | undefined) {
  if (value === null || value === undefined) {
    return ""
  }

  const normalized = String(value)
  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`
  }

  return normalized
}

function downloadCsvFile(fileName: string, rows: Array<Array<string | number | null | undefined>>) {
  if (typeof window === "undefined") {
    return
  }

  const csv = rows.map((row) => row.map((cell) => encodeCsvCell(cell)).join(",")).join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  window.URL.revokeObjectURL(url)
}

function buildDownloadSlug(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
}

function buildAnalyticsSearchPayload(search: OrchardWorkspaceSearch) {
  return {
    dateFrom: normalizeAnalyticsDateValue(search.analyticsFrom),
    dateTo: normalizeAnalyticsDateValue(search.analyticsTo),
  }
}

function buildAnalyticsDateRangeLabel(search: OrchardWorkspaceSearch) {
  const dateFrom = normalizeAnalyticsDateValue(search.analyticsFrom)
  const dateTo = normalizeAnalyticsDateValue(search.analyticsTo)

  if (dateFrom && dateTo) {
    return `${dateFrom} to ${dateTo}`
  }

  if (dateFrom) {
    return `from ${dateFrom}`
  }

  if (dateTo) {
    return `until ${dateTo}`
  }

  return "all_time"
}

function OrchardWorkspaceSkeleton() {
  return (
    <OperatorWorkspaceLayout
      badge="GrowBetter Orchard Intelligence"
      links={ORCHARD_SECTION_OPTIONS.map((option) => ({
        active: option.value === "overview",
        description: option.description,
        label: option.label,
        to: "/orchards",
      }))}
    >
      <OperatorPanel
        title="Orchard portfolio"
        description="Loading orchard scope, summary metrics, and orchard records."
      >
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`orchard-skeleton-metric-${index}`}
              className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-3 h-8 w-14" />
              <Skeleton className="mt-3 h-4 w-full" />
            </div>
          ))}
        </div>
      </OperatorPanel>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
        <OperatorPanel
          title="Create orchard"
          description="Preparing orchard registration fields."
        >
          <div className="grid gap-4 md:grid-cols-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={`orchard-skeleton-field-${index}`} className="grid gap-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full rounded-2xl" />
              </div>
            ))}
          </div>
        </OperatorPanel>
        <OperatorPanel
          title="Orchard records"
          description="Preparing orchard detail and scope."
        >
          <div className="grid gap-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={`orchard-skeleton-record-${index}`}
                className="h-24 w-full rounded-3xl"
              />
            ))}
          </div>
        </OperatorPanel>
      </div>
    </OperatorWorkspaceLayout>
  )
}

function parseOptionalNumber(value: string) {
  const normalized = value.trim()

  if (!normalized) {
    return null
  }

  const parsed = Number.parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function parseOptionalInteger(value: string) {
  const normalized = value.trim()

  if (!normalized) {
    return null
  }

  const parsed = Number.parseInt(normalized, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function buildCreateRequest(
  form: OrchardCreateFormState,
  organizationId?: string
): OrchardCreateRequest {
  return {
    areaAcres: parseOptionalNumber(form.areaAcres),
    cropType: form.cropType.trim() || "mango",
    district: form.district.trim() || null,
    managerName: form.managerName.trim() || null,
    managerPhone: form.managerPhone.trim() || null,
    name: form.name.trim(),
    organizationId,
    ownerName: form.ownerName.trim() || null,
    plantingYear: parseOptionalInteger(form.plantingYear),
    primaryVariety: form.primaryVariety.trim() || null,
    state: form.state.trim() || null,
    taluk: form.taluk.trim() || null,
    village: form.village.trim() || null,
  }
}

function normalizeTokenPart(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function buildBlockRequest(form: OrchardBlockFormState): OrchardBlockCreateRequest {
  return {
    code: form.code.trim(),
    name: form.name.trim(),
    sequence: parseOptionalInteger(form.sequence),
    varietyOverride: form.varietyOverride.trim() || null,
  }
}

function buildRowRequest(form: OrchardRowFormState): OrchardRowCreateRequest {
  return {
    blockId: form.blockId,
    code: form.code.trim(),
    orientation: form.orientation.trim() || null,
    sequence: parseOptionalInteger(form.sequence),
  }
}

function buildSuggestedQrToken(
  orchard: OrchardDetailRecord | null,
  block: OrchardBlockRecord | null,
  row: OrchardRowRecord | null,
  treeCode: string
) {
  return [
    orchard?.id,
    normalizeTokenPart(block?.code || ""),
    normalizeTokenPart(row?.code || ""),
    normalizeTokenPart(treeCode),
  ]
    .filter(Boolean)
    .join("-")
}

function buildTreeRequest(
  form: OrchardTreeFormState,
  orchard: OrchardDetailRecord | null,
  block: OrchardBlockRecord | null,
  row: OrchardRowRecord | null
): OrchardTreeCreateRequest {
  const qrToken =
    form.qrToken.trim()
    || buildSuggestedQrToken(orchard, block, row, form.treeCode)

  return {
    ageYears: parseOptionalInteger(form.ageYears),
    blockId: form.blockId,
    plantingYear: parseOptionalInteger(form.plantingYear),
    qrToken,
    rowId: form.rowId || null,
    status: form.status.trim() || "active",
    treeCode: form.treeCode.trim(),
    variety: form.variety.trim() || null,
  }
}

function loadQrCodeLibrary() {
  if (typeof window === "undefined") {
    return Promise.resolve()
  }

  if (typeof window.QRCode === "function") {
    return Promise.resolve()
  }

  if (qrCodeLibraryPromise) {
    return qrCodeLibraryPromise
  }

  qrCodeLibraryPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="/admin/vendor/qrcode.min.js"]'
    )

    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true })
      existing.addEventListener(
        "error",
        () => reject(new Error("Unable to load QRCode renderer")),
        { once: true }
      )
      return
    }

    const script = document.createElement("script")
    script.src = "/admin/vendor/qrcode.min.js"
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Unable to load QRCode renderer"))
    document.head.appendChild(script)
  })

  return qrCodeLibraryPromise
}

function stretchQrMarkup(container: HTMLDivElement) {
  const rootNode = container.firstElementChild

  if (!(rootNode instanceof HTMLElement)) {
    return
  }

  rootNode.style.width = "100%"
  rootNode.style.height = "100%"
  rootNode.style.display = "flex"
  rootNode.style.alignItems = "center"
  rootNode.style.justifyContent = "center"

  for (const node of rootNode.querySelectorAll("img, canvas, table")) {
    if (node instanceof HTMLElement) {
      node.style.width = "100%"
      node.style.height = "100%"
      node.style.maxWidth = "100%"
      node.style.maxHeight = "100%"
      node.style.objectFit = "contain"
    }
  }
}

function buildOrchardTreePublicUrl(qrToken: string) {
  const pathname = `/orchard/tree/${encodeURIComponent(qrToken)}`

  if (typeof window === "undefined") {
    return pathname
  }

  return new URL(pathname, window.location.origin).toString()
}

function OrchardPrintQrCode(props: { qrToken: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  useEffect(() => {
    let isActive = true

    async function renderQr() {
      const container = containerRef.current

      if (!container) {
        return
      }

      container.innerHTML = ""
      setRenderError(null)

      try {
        await loadQrCodeLibrary()

        if (!isActive || typeof window.QRCode !== "function") {
          return
        }

        container.innerHTML = ""
        new window.QRCode(container, {
          colorDark: "#163029",
          colorLight: "#ffffff",
          correctLevel: window.QRCode.CorrectLevel?.M,
          height: 180,
          text: buildOrchardTreePublicUrl(props.qrToken),
          width: 180,
        })
        stretchQrMarkup(container)
      } catch (error) {
        if (!isActive) {
          return
        }

        setRenderError(
          error instanceof Error ? error.message : "QR rendering failed."
        )
      }
    }

    void renderQr()

    return () => {
      isActive = false
      if (containerRef.current) {
        containerRef.current.innerHTML = ""
      }
    }
  }, [props.qrToken])

  return (
    <div className="flex aspect-square w-full items-center justify-center rounded-[1.75rem] border border-emerald-200 bg-white p-4 shadow-[inset_0_0_0_1px_rgba(22,48,41,0.06)]">
      <div
        ref={containerRef}
        aria-label={`QR for ${props.qrToken}`}
        className="flex h-full w-full items-center justify-center overflow-hidden rounded-[1.25rem]"
      />
      {renderError ? (
        <span className="text-center text-xs leading-5 text-muted-foreground">
          QR preview unavailable in this environment.
        </span>
      ) : null}
    </div>
  )
}

function formatTreePlacement(tree: OrchardTreeRecord) {
  const placement = [`Block ${tree.block?.code || "—"}`]

  if (tree.row?.code) {
    placement.push(`Row ${tree.row.code}`)
  }

  return placement.join(" · ")
}

function OrchardQrExportCard(props: {
  orchardName: string
  primaryVariety?: string | null
  tree: OrchardTreeRecord
}) {
  const variety = props.tree.variety || props.primaryVariety || "Variety pending"

  return (
    <div className="orchard-print-card rounded-[2rem] border border-[#d7e5d5] bg-[linear-gradient(180deg,#fcfdf8_0%,#f5f7ee_100%)] px-4 py-4 shadow-[0_10px_30px_rgba(22,48,41,0.08)]">
      <div className="grid gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="grid gap-2">
            <div className="inline-flex w-fit items-center rounded-full border border-[#d8e4cf] bg-[#eef4e4] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#3d5a35]">
              GrowBetter Orchard Intelligence
            </div>
            <div>
              <div className="text-[1.2rem] font-semibold tracking-[-0.02em] text-[#163029]">
                {props.tree.treeCode}
              </div>
              <div className="mt-1 text-sm text-[#506451]">
                {props.orchardName}
              </div>
            </div>
          </div>

          <div className="rounded-full border border-[#cfe0ca] bg-white/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#5d705e]">
            {props.tree.status}
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-[#d9e4d2] bg-white px-4 py-4 shadow-[inset_0_0_0_1px_rgba(22,48,41,0.04)]">
          <OrchardPrintQrCode qrToken={props.tree.qrToken} />
        </div>

        <div className="grid gap-3 rounded-[1.5rem] border border-[#dbe5d4] bg-white/78 px-4 py-3 text-sm text-[#294033]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#eaf1df] px-2.5 py-1 text-[11px] font-medium text-[#3f5d3f]">
              {formatTreePlacement(props.tree)}
            </span>
            <span className="rounded-full bg-[#f2efe0] px-2.5 py-1 text-[11px] font-medium text-[#6a5c2f]">
              {variety}
            </span>
          </div>

          <div className="grid gap-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6d7b69]">
              Scan use
            </div>
            <div className="text-sm leading-6 text-[#314735]">
              Record flowering, fruit load, harvest, and tree health observations.
            </div>
          </div>

          <div className="grid gap-1 border-t border-[#e5ebdf] pt-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6d7b69]">
              Tree token
            </div>
            <div className="break-all font-mono text-[11px] leading-5 text-[#5b685d]">
              {props.tree.qrToken}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function OrchardWorkspace({
  onSearchChange,
  search,
}: OrchardWorkspaceProps) {
  const sessionState = useOperatorShellSession()
  const [bundle, setBundle] = useState<OrchardWorkspaceBundle | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedOrchard, setSelectedOrchard] = useState<OrchardDetailRecord | null>(null)
  const [selectedAnalytics, setSelectedAnalytics] =
    useState<OrchardAnalyticsRecord | null>(null)
  const [portfolioAnalytics, setPortfolioAnalytics] =
    useState<OrchardPortfolioAnalyticsRecord | null>(null)
  const [selectedOrchardError, setSelectedOrchardError] = useState<string | null>(
    null
  )
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false)
  const [isPortfolioAnalyticsLoading, setIsPortfolioAnalyticsLoading] = useState(false)
  const [analyticsError, setAnalyticsError] = useState<string | null>(null)
  const [portfolioAnalyticsError, setPortfolioAnalyticsError] = useState<string | null>(
    null
  )
  const [orchardTrees, setOrchardTrees] = useState<OrchardTreeRecord[]>([])
  const [treeError, setTreeError] = useState<string | null>(null)
  const [isTreeLoading, setIsTreeLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isBlockSubmitting, setIsBlockSubmitting] = useState(false)
  const [isRowSubmitting, setIsRowSubmitting] = useState(false)
  const [isTreeSubmitting, setIsTreeSubmitting] = useState(false)
  const [isTreeImporting, setIsTreeImporting] = useState(false)
  const [treeImportMode, setTreeImportMode] = useState<"dry_run" | "import" | null>(
    null
  )
  const [submitNotice, setSubmitNotice] = useState<string | null>(null)
  const [treeImportCsvText, setTreeImportCsvText] = useState("")
  const [treeImportSummary, setTreeImportSummary] =
    useState<OrchardTreeImportSummary | null>(null)
  const [createForm, setCreateForm] = useState<OrchardCreateFormState>(
    INITIAL_CREATE_FORM
  )
  const [blockForm, setBlockForm] = useState<OrchardBlockFormState>(INITIAL_BLOCK_FORM)
  const [rowForm, setRowForm] = useState<OrchardRowFormState>(INITIAL_ROW_FORM)
  const [treeForm, setTreeForm] = useState<OrchardTreeFormState>(INITIAL_TREE_FORM)
  const [qrExportBlockId, setQrExportBlockId] = useState("")
  const section = getSectionValue(search.section)
  const analyticsSearch = buildAnalyticsSearchPayload(search)

  function buildWorkspaceHref(next: Partial<OrchardWorkspaceSearch>) {
    const params = new URLSearchParams()
    const nextSearch: OrchardWorkspaceSearch = {
      ...search,
      ...next,
    }

    if (nextSearch.section && nextSearch.section !== "overview") {
      params.set("section", nextSearch.section)
    }

    if (nextSearch.organizationId) {
      params.set("organizationId", nextSearch.organizationId)
    }

    if (nextSearch.orchardId) {
      params.set("orchardId", nextSearch.orchardId)
    }

    if (nextSearch.analyticsFrom) {
      params.set("analyticsFrom", nextSearch.analyticsFrom)
    }

    if (nextSearch.analyticsTo) {
      params.set("analyticsTo", nextSearch.analyticsTo)
    }

    const query = params.toString()
    return query ? `/orchards?${query}` : "/orchards"
  }

  const orchardLinks = ORCHARD_SECTION_OPTIONS.map((option) => ({
    active: section === option.value,
    description: option.description,
    label: option.label,
    to: buildWorkspaceHref({
      section: option.value === "overview" ? undefined : option.value,
    }),
  }))

  useEffect(() => {
    if (sessionState.status !== "ready") {
      return
    }

    if (!canAccessOrchardWorkspace(sessionState.session)) {
      startTransition(() => {
        setBundle(null)
        setErrorMessage("This session does not have access to the orchard workspace.")
        setIsLoading(false)
      })
      return
    }

    const isPortalAdmin = canManagePortalOrganizations(sessionState.session)
    const scopedOrganizationId = search.organizationId || sessionState.session.organizationId || undefined
    let isActive = true

    async function loadBundle() {
      startTransition(() => {
        setIsLoading(true)
        setErrorMessage(null)
        setSubmitNotice(null)
      })

      try {
        const organizationsPromise = isPortalAdmin
          ? apiClient.getOrganizations()
          : Promise.resolve({
              data: [] as OperatorOrganizationRecord[],
              success: true,
            })
        const orchardsPromise = apiClient.getOrchards({
          organizationId: scopedOrganizationId,
        })

        const [organizationsResult, orchardsResult] = await Promise.all([
          organizationsPromise,
          orchardsPromise,
        ])

        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle({
            organizations: organizationsResult.data ?? [],
            orchards: orchardsResult.data ?? [],
          })
          setIsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setBundle(null)
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to load the orchard workspace."
          )
          setIsLoading(false)
        })
      }
    }

    void loadBundle()

    return () => {
      isActive = false
    }
  }, [search.organizationId, sessionState])

  const selectedOrganizationId =
    search.organizationId || (sessionState.status === "ready"
      ? sessionState.session.organizationId || undefined
      : undefined)

  const selectedOrchardId = search.orchardId || bundle?.orchards[0]?.id

  useEffect(() => {
    if (sessionState.status !== "ready" || !bundle || !selectedOrchardId) {
      startTransition(() => {
        setSelectedOrchard(null)
        setSelectedAnalytics(null)
        setAnalyticsError(null)
        setSelectedOrchardError(null)
        setIsDetailLoading(false)
        setIsAnalyticsLoading(false)
      })
      return
    }

    let isActive = true

    async function loadDetail() {
      const orchardId = selectedOrchardId

      if (!orchardId) {
        return
      }

      startTransition(() => {
        setIsDetailLoading(true)
        setSelectedOrchardError(null)
      })

      try {
        const orchard = await apiClient.getOrchard(orchardId, {
          organizationId: selectedOrganizationId,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedOrchard(orchard.data)
          setIsDetailLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedOrchard(null)
          setSelectedOrchardError(
            error instanceof Error
              ? error.message
              : "Failed to load orchard detail."
          )
          setIsDetailLoading(false)
        })
      }
    }

    void loadDetail()

    return () => {
      isActive = false
    }
  }, [bundle, selectedOrchardId, selectedOrganizationId, sessionState])

  useEffect(() => {
    if (sessionState.status !== "ready" || !selectedOrchardId) {
      startTransition(() => {
        setSelectedAnalytics(null)
        setAnalyticsError(null)
        setIsAnalyticsLoading(false)
      })
      return
    }

    let isActive = true

    async function loadAnalytics() {
      const orchardId = selectedOrchardId

      if (!orchardId) {
        return
      }

      startTransition(() => {
        setIsAnalyticsLoading(true)
        setAnalyticsError(null)
      })

      try {
        const analytics = await apiClient.getOrchardAnalytics(orchardId, {
          dateFrom: analyticsSearch.dateFrom,
          dateTo: analyticsSearch.dateTo,
          organizationId: selectedOrganizationId,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedAnalytics(analytics.data)
          setIsAnalyticsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setSelectedAnalytics(null)
          setAnalyticsError(
            error instanceof Error
              ? error.message
              : "Failed to load orchard analytics."
          )
          setIsAnalyticsLoading(false)
        })
      }
    }

    void loadAnalytics()

    return () => {
      isActive = false
    }
  }, [
    analyticsSearch.dateFrom,
    analyticsSearch.dateTo,
    selectedOrchardId,
    selectedOrganizationId,
    sessionState,
  ])

  const metrics = useMemo(() => {
    const orchards = bundle?.orchards ?? []

    return orchards.reduce(
      (summary, orchard) => {
        summary.orchards += 1
        summary.blocks += orchard._count?.blocks ?? 0
        summary.observations += orchard._count?.observations ?? 0
        summary.trees += orchard._count?.trees ?? 0
        return summary
      },
      {
        blocks: 0,
        observations: 0,
        orchards: 0,
        trees: 0,
      }
    )
  }, [bundle])

  const selectedOrganizationLabel = useMemo(() => {
    if (sessionState.status !== "ready") {
      return "Loading scope"
    }

    if (!canManagePortalOrganizations(sessionState.session)) {
      return sessionState.session.organizationName || "Scoped organization"
    }

    if (!selectedOrganizationId) {
      return "All organizations"
    }

    return (
      bundle?.organizations.find((item) => item.id === selectedOrganizationId)?.name
      || selectedOrganizationId
    )
  }, [bundle, selectedOrganizationId, sessionState])

  const selectedBlock = useMemo(
    () =>
      selectedOrchard?.blocks?.find((block) => block.id === treeForm.blockId)
      || selectedOrchard?.blocks?.[0]
      || null,
    [selectedOrchard, treeForm.blockId]
  )

  const availableRows = useMemo(
    () => selectedBlock?.rows ?? [],
    [selectedBlock]
  )

  const selectedRow = useMemo(
    () => availableRows.find((row) => row.id === treeForm.rowId) || null,
    [availableRows, treeForm.rowId]
  )

  const qrExportBlockOptions = useMemo(
    () => selectedOrchard?.blocks ?? [],
    [selectedOrchard]
  )

  const visibleQrExportTrees = useMemo(() => {
    const filteredTrees = qrExportBlockId
      ? orchardTrees.filter((tree) => tree.blockId === qrExportBlockId)
      : orchardTrees

    return [...filteredTrees].sort((left, right) => {
      const leftBlock = left.block?.code || ""
      const rightBlock = right.block?.code || ""
      if (leftBlock !== rightBlock) {
        return leftBlock.localeCompare(rightBlock)
      }

      const leftRow = left.row?.code || ""
      const rightRow = right.row?.code || ""
      if (leftRow !== rightRow) {
        return leftRow.localeCompare(rightRow)
      }

      return left.treeCode.localeCompare(right.treeCode)
    })
  }, [orchardTrees, qrExportBlockId])

  const observationTypeEntries = useMemo(
    () =>
      selectedAnalytics
        ? Object.entries(selectedAnalytics.observationTypeCounts).filter(
            ([, count]) => count > 0
          )
        : [],
    [selectedAnalytics]
  )

  const monthlyActivityMax = useMemo(
    () =>
      selectedAnalytics
        ? Math.max(
            ...selectedAnalytics.monthlyActivity.map(
              (item) => item.observationCount
            ),
            1
          )
        : 1,
    [selectedAnalytics]
  )

  const performanceFlags = selectedAnalytics?.performanceFlags ?? null
  const portfolioMonthlyActivityMax = useMemo(
    () =>
      portfolioAnalytics
        ? Math.max(
            ...portfolioAnalytics.monthlyActivity.map(
              (item) => item.observationCount
            ),
            1
          )
        : 1,
    [portfolioAnalytics]
  )

  useEffect(() => {
    if (sessionState.status !== "ready") {
      startTransition(() => {
        setPortfolioAnalytics(null)
        setPortfolioAnalyticsError(null)
        setIsPortfolioAnalyticsLoading(false)
      })
      return
    }

    if (!canAccessOrchardWorkspace(sessionState.session)) {
      startTransition(() => {
        setPortfolioAnalytics(null)
        setPortfolioAnalyticsError("This session does not have access to the orchard workspace.")
        setIsPortfolioAnalyticsLoading(false)
      })
      return
    }

    let isActive = true

    async function loadPortfolioAnalytics() {
      startTransition(() => {
        setIsPortfolioAnalyticsLoading(true)
        setPortfolioAnalyticsError(null)
      })

      try {
        const analytics = await apiClient.getOrchardPortfolioAnalytics({
          dateFrom: analyticsSearch.dateFrom,
          dateTo: analyticsSearch.dateTo,
          organizationId: selectedOrganizationId,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setPortfolioAnalytics(analytics.data)
          setIsPortfolioAnalyticsLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setPortfolioAnalytics(null)
          setPortfolioAnalyticsError(
            error instanceof Error
              ? error.message
              : "Failed to load orchard portfolio analytics."
          )
          setIsPortfolioAnalyticsLoading(false)
        })
      }
    }

    void loadPortfolioAnalytics()

    return () => {
      isActive = false
    }
  }, [
    analyticsSearch.dateFrom,
    analyticsSearch.dateTo,
    selectedOrganizationId,
    sessionState,
  ])

  useEffect(() => {
    if (!selectedOrchard) {
      startTransition(() => {
        setBlockForm(INITIAL_BLOCK_FORM)
        setRowForm(INITIAL_ROW_FORM)
        setTreeForm(INITIAL_TREE_FORM)
        setQrExportBlockId("")
        setTreeImportSummary(null)
      })
      return
    }

    startTransition(() => {
      setRowForm((current) => ({
        ...current,
        blockId:
          current.blockId
          && selectedOrchard.blocks?.some((block) => block.id === current.blockId)
            ? current.blockId
            : selectedOrchard.blocks?.[0]?.id || "",
      }))
      setTreeForm((current) => {
        const nextBlockId =
          current.blockId
          && selectedOrchard.blocks?.some((block) => block.id === current.blockId)
            ? current.blockId
            : selectedOrchard.blocks?.[0]?.id || ""
        const nextRows =
          selectedOrchard.blocks?.find((block) => block.id === nextBlockId)?.rows ?? []
        const nextRowId =
          current.rowId && nextRows.some((row) => row.id === current.rowId)
            ? current.rowId
            : ""

        return {
          ...current,
          blockId: nextBlockId,
          rowId: nextRowId,
        }
      })
      setQrExportBlockId((current) =>
        current && selectedOrchard.blocks?.some((block) => block.id === current)
          ? current
          : ""
      )
    })
  }, [selectedOrchard])

  useEffect(() => {
    if (sessionState.status !== "ready" || !selectedOrchardId) {
      startTransition(() => {
        setOrchardTrees([])
        setTreeError(null)
        setIsTreeLoading(false)
      })
      return
    }

    let isActive = true

    async function loadTrees() {
      const orchardId = selectedOrchardId

      if (!orchardId) {
        return
      }

      startTransition(() => {
        setIsTreeLoading(true)
        setTreeError(null)
      })

      try {
        const trees = await apiClient.getOrchardTrees(orchardId, {
          organizationId: selectedOrganizationId,
        })

        if (!isActive) {
          return
        }

        startTransition(() => {
          setOrchardTrees(trees.data ?? [])
          setIsTreeLoading(false)
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setOrchardTrees([])
          setTreeError(
            error instanceof Error
              ? error.message
              : "Failed to load orchard trees."
          )
          setIsTreeLoading(false)
        })
      }
    }

    void loadTrees()

    return () => {
      isActive = false
    }
  }, [selectedOrchardId, selectedOrganizationId, sessionState])

  if (sessionState.status === "loading") {
    return <OrchardWorkspaceSkeleton />
  }

  if (sessionState.status === "error") {
    return (
      <OperatorWorkspaceLayout
        badge="GrowBetter Orchard Intelligence"
        links={orchardLinks}
      >
        <OperatorStatusMessage tone="danger">
          {sessionState.message}
        </OperatorStatusMessage>
      </OperatorWorkspaceLayout>
    )
  }

  if (!canAccessOrchardWorkspace(sessionState.session)) {
    return (
      <OperatorWorkspaceLayout
        badge="GrowBetter Orchard Intelligence"
        links={orchardLinks}
      >
        <OperatorStatusMessage tone="danger">
          Your current operator scope does not include orchard workspace access.
        </OperatorStatusMessage>
      </OperatorWorkspaceLayout>
    )
  }

  const readySession = sessionState.session

  async function refreshOrchardRegistry(nextOrchardId?: string) {
    const orchards = await apiClient.getOrchards({
      organizationId: selectedOrganizationId,
    })

    startTransition(() => {
      setBundle((current) => ({
        organizations: current?.organizations ?? [],
        orchards: orchards.data ?? [],
      }))
    })

    if (nextOrchardId) {
      onSearchChange({
        orchardId: nextOrchardId,
        section: "orchards",
      })
    }
  }

  async function refreshSelectedOrchard() {
    if (!selectedOrchardId) {
      return
    }

    const orchard = await apiClient.getOrchard(selectedOrchardId, {
      organizationId: selectedOrganizationId,
    })

    startTransition(() => {
      setSelectedOrchard(orchard.data)
      setSelectedOrchardError(null)
    })
  }

  async function refreshSelectedAnalytics() {
    if (!selectedOrchardId) {
      return
    }

    const analytics = await apiClient.getOrchardAnalytics(selectedOrchardId, {
      dateFrom: analyticsSearch.dateFrom,
      dateTo: analyticsSearch.dateTo,
      organizationId: selectedOrganizationId,
    })

    startTransition(() => {
      setSelectedAnalytics(analytics.data)
      setAnalyticsError(null)
    })
  }

  async function refreshPortfolioAnalytics() {
    const analytics = await apiClient.getOrchardPortfolioAnalytics({
      dateFrom: analyticsSearch.dateFrom,
      dateTo: analyticsSearch.dateTo,
      organizationId: selectedOrganizationId,
    })

    startTransition(() => {
      setPortfolioAnalytics(analytics.data)
      setPortfolioAnalyticsError(null)
    })
  }

  async function refreshSelectedOrchardTrees() {
    if (!selectedOrchardId) {
      return
    }

    const trees = await apiClient.getOrchardTrees(selectedOrchardId, {
      organizationId: selectedOrganizationId,
    })

    startTransition(() => {
      setOrchardTrees(trees.data ?? [])
      setTreeError(null)
    })
  }

  function handleApplyAnalyticsRange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSearchChange({
      analyticsFrom: normalizeAnalyticsDateValue(search.analyticsFrom),
      analyticsTo: normalizeAnalyticsDateValue(search.analyticsTo),
      section: "overview",
    })
  }

  function handleResetAnalyticsRange() {
    onSearchChange({
      analyticsFrom: undefined,
      analyticsTo: undefined,
      section: "overview",
    })
  }

  function handleDownloadAnalyticsCsv() {
    if (!selectedAnalytics || !selectedOrchard) {
      return
    }

    const rows: Array<Array<string | number | null | undefined>> = [
      ["section", "metric", "value", "detail_1", "detail_2", "detail_3"],
      [
        "summary",
        "date_range",
        buildAnalyticsDateRangeLabel(search),
        selectedAnalytics.dateFrom,
        selectedAnalytics.dateTo,
        null,
      ],
      ["summary", "observed_trees", selectedAnalytics.observedTreeCount, null, null, null],
      ["summary", "pending_observation_trees", selectedAnalytics.pendingObservationTreeCount, null, null, null],
      ["summary", "active_trees", selectedAnalytics.activeTreeCount, null, null, null],
      ["summary", "tree_count", selectedAnalytics.treeCount, null, null, null],
      ["summary", "total_observations", selectedAnalytics.totalObservations, null, null, null],
      ["summary", "latest_observation_at", selectedAnalytics.latestObservationAt, null, null, null],
      ["summary", "latest_harvest_at", selectedAnalytics.seasonalSummary.latestHarvestAt, null, null, null],
      ["summary", "latest_harvest_round", selectedAnalytics.seasonalSummary.latestHarvestRound, null, null, null],
    ]

    for (const [type, count] of Object.entries(selectedAnalytics.observationTypeCounts)) {
      rows.push(["observation_mix", type, count, null, null, null])
    }

    rows.push(
      ["seasonal_summary", "flowering_entries", selectedAnalytics.seasonalSummary.floweringEntries, null, null, null],
      ["seasonal_summary", "fruit_set_entries", selectedAnalytics.seasonalSummary.fruitSetEntries, null, null, null],
      ["seasonal_summary", "fruit_load_entries", selectedAnalytics.seasonalSummary.fruitLoadEntries, null, null, null],
      ["seasonal_summary", "harvest_entries", selectedAnalytics.seasonalSummary.harvestEntries, null, null, null],
      ["seasonal_summary", "health_check_entries", selectedAnalytics.seasonalSummary.healthCheckEntries, null, null, null],
    )

    rows.push(
      ["seasonal_comparison_current", "total_observations", selectedAnalytics.seasonalComparison.current.totalObservations, selectedAnalytics.seasonalComparison.current.startAt, selectedAnalytics.seasonalComparison.current.endAt, null],
      ["seasonal_comparison_previous", "total_observations", selectedAnalytics.seasonalComparison.previous.totalObservations, selectedAnalytics.seasonalComparison.previous.startAt, selectedAnalytics.seasonalComparison.previous.endAt, null],
      ["seasonal_comparison_delta", "total_observations", selectedAnalytics.seasonalComparison.delta.totalObservations, null, null, null],
      ["seasonal_comparison_delta", "harvest_entries", selectedAnalytics.seasonalComparison.delta.harvestEntries, null, null, null],
      ["seasonal_comparison_delta", "flowering_entries", selectedAnalytics.seasonalComparison.delta.floweringEntries, null, null, null],
      ["seasonal_comparison_delta", "fruit_set_entries", selectedAnalytics.seasonalComparison.delta.fruitSetEntries, null, null, null],
      ["seasonal_comparison_delta", "fruit_load_entries", selectedAnalytics.seasonalComparison.delta.fruitLoadEntries, null, null, null],
      ["seasonal_comparison_delta", "health_check_entries", selectedAnalytics.seasonalComparison.delta.healthCheckEntries, null, null, null],
    )

    for (const point of selectedAnalytics.monthlyActivity) {
      rows.push(["monthly_activity", point.monthKey, point.observationCount, point.monthLabel, null, null])
    }

    for (const entry of selectedAnalytics.harvestYieldByUnit) {
      rows.push(["harvest_totals", entry.unit, entry.totalQuantity, entry.entryCount, null, null])
    }

    for (const block of selectedAnalytics.blockActivity) {
      rows.push(["block_activity", block.blockId, block.observationCount, block.blockCode, block.blockName, null])
    }

    for (const tree of selectedAnalytics.performanceFlags.treesWithNoRecentHarvest) {
      rows.push(["flag_no_recent_harvest", tree.treeId, tree.treeCode, tree.blockCode, tree.rowCode, null])
    }

    for (const block of selectedAnalytics.performanceFlags.blocksWithLowObservationCoverage) {
      rows.push([
        "flag_low_coverage_block",
        block.blockId,
        block.blockCode,
        block.blockName,
        formatPercentage(block.coverageRatio),
        `${block.observedTreeCount}/${block.totalTreeCount}`,
      ])
    }

    for (const tree of selectedAnalytics.performanceFlags.inactiveButStillScanned) {
      rows.push(["flag_inactive_scanned", tree.treeId, tree.treeCode, tree.blockCode, tree.rowCode, null])
    }

    for (const tree of selectedAnalytics.performanceFlags.lowPerformingTrees) {
      rows.push([
        "flag_low_performing_tree",
        tree.treeId,
        tree.treeCode,
        tree.averageHarvestQuantityPerEntry,
        tree.orchardBaselineQuantity,
        tree.primaryHarvestUnit,
      ])
    }

    for (const block of selectedAnalytics.performanceFlags.blockAnomalies) {
      rows.push([
        "flag_block_anomaly",
        block.blockId,
        block.blockCode,
        block.anomalyReasons.join(" | "),
        block.averageHarvestQuantityPerEntry,
        block.primaryHarvestUnit,
      ])
    }

    for (const insight of selectedAnalytics.managerSummary) {
      rows.push(["manager_summary", "insight", insight, null, null, null])
    }

    downloadCsvFile(
      `${selectedOrchard.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-analytics.csv`,
      rows
    )
  }

  function handleDownloadTreeImportErrorsCsv() {
    if (!treeImportSummary?.errors.length) {
      return
    }

    downloadCsvFile("orchard-tree-import-errors.csv", [
      ["row_number", "message", "block_code", "row_code", "tree_code", "variety", "status", "planting_year", "age_years", "qr_token"],
      ...treeImportSummary.errors.map((error) => {
        const row = error.row ?? {}
        return [
          error.rowNumber,
          error.message,
          typeof row.block_code === "string" ? row.block_code : "",
          typeof row.row_code === "string" ? row.row_code : "",
          typeof row.tree_code === "string" ? row.tree_code : "",
          typeof row.variety === "string" ? row.variety : "",
          typeof row.status === "string" ? row.status : "",
          typeof row.planting_year === "string" ? row.planting_year : "",
          typeof row.age_years === "string" ? row.age_years : "",
          typeof row.qr_token === "string" ? row.qr_token : "",
        ]
      }),
    ])
  }

  function handleDownloadPortfolioComparisonCsv() {
    if (!portfolioAnalytics) {
      return
    }

    const rows: Array<Array<string | number | null | undefined>> = [[
      "organization_scope",
      "date_range",
      "orchard_id",
      "orchard_name",
      "dominant_variety",
      "primary_variety",
      "age_band",
      "average_tree_age_years",
      "tree_count",
      "observed_tree_count",
      "pending_observation_tree_count",
      "observation_coverage_ratio",
      "active_tree_count",
      "total_observations",
      "harvest_entries",
      "average_harvest_quantity_per_entry",
      "primary_harvest_unit",
      "total_harvest_quantity",
      "yield_pattern",
      "latest_observation_at",
    ]]

    for (const orchard of portfolioAnalytics.orchardComparisons) {
      rows.push([
        selectedOrganizationLabel,
        buildAnalyticsDateRangeLabel(search),
        orchard.orchardId,
        orchard.orchardName,
        orchard.dominantVariety,
        orchard.primaryVariety,
        orchard.ageBandLabel,
        orchard.averageTreeAgeYears,
        orchard.treeCount,
        orchard.observedTreeCount,
        orchard.pendingObservationTreeCount,
        orchard.observationCoverageRatio,
        orchard.activeTreeCount,
        orchard.totalObservations,
        orchard.harvestEntries,
        orchard.averageHarvestQuantityPerEntry,
        orchard.primaryHarvestUnit,
        orchard.totalHarvestQuantity,
        orchard.yieldPatternLabel,
        orchard.latestObservationAt,
      ])
    }

    downloadCsvFile(
      `${buildDownloadSlug(selectedOrganizationLabel || "orchard-portfolio")}-portfolio-comparison-${buildDownloadSlug(buildAnalyticsDateRangeLabel(search))}.csv`,
      rows
    )
  }

  function handleDownloadPortfolioBenchmarksCsv() {
    if (!portfolioAnalytics) {
      return
    }

    const rows: Array<Array<string | number | null | undefined>> = [[
      "benchmark_type",
      "group_key",
      "group_label",
      "organization_scope",
      "date_range",
      "orchard_count",
      "average_observation_coverage_ratio",
      "average_harvest_quantity_per_entry",
      "primary_harvest_unit",
      "top_orchard_id",
      "top_orchard_name",
      "top_orchard_total_observations",
      "top_orchard_coverage_ratio",
      "top_orchard_yield_pattern",
    ]]

    const benchmarkGroups = [
      {
        groups: portfolioAnalytics.varietyBenchmarks,
        type: "variety",
      },
      {
        groups: portfolioAnalytics.ageBandBenchmarks,
        type: "age_band",
      },
      {
        groups: portfolioAnalytics.yieldPatternBenchmarks,
        type: "yield_pattern",
      },
    ]

    for (const benchmark of benchmarkGroups) {
      for (const group of benchmark.groups) {
        if (!group.topOrchards.length) {
          rows.push([
            benchmark.type,
            group.groupKey,
            group.groupLabel,
            selectedOrganizationLabel,
            buildAnalyticsDateRangeLabel(search),
            group.orchardCount,
            group.averageObservationCoverageRatio,
            group.averageHarvestQuantityPerEntry,
            group.primaryHarvestUnit,
            null,
            null,
            null,
            null,
            null,
          ])
          continue
        }

        for (const orchard of group.topOrchards) {
          rows.push([
            benchmark.type,
            group.groupKey,
            group.groupLabel,
            selectedOrganizationLabel,
            buildAnalyticsDateRangeLabel(search),
            group.orchardCount,
            group.averageObservationCoverageRatio,
            group.averageHarvestQuantityPerEntry,
            group.primaryHarvestUnit,
            orchard.orchardId,
            orchard.orchardName,
            orchard.totalObservations,
            orchard.observationCoverageRatio,
            orchard.yieldPatternLabel,
          ])
        }
      }
    }

    downloadCsvFile(
      `${buildDownloadSlug(selectedOrganizationLabel || "orchard-portfolio")}-portfolio-benchmarks-${buildDownloadSlug(buildAnalyticsDateRangeLabel(search))}.csv`,
      rows
    )
  }

  async function handleCreateOrchard(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (
      canManagePortalOrganizations(readySession) &&
      !selectedOrganizationId
    ) {
      setErrorMessage(
        "Select an organization before creating an orchard record."
      )
      return
    }

    const payload = buildCreateRequest(createForm, selectedOrganizationId)

    if (!payload.name) {
      setErrorMessage("Orchard name is required.")
      return
    }

    startTransition(() => {
      setIsSubmitting(true)
      setErrorMessage(null)
      setSubmitNotice(null)
    })

    try {
      const created = await apiClient.createOrchard(payload)
      await Promise.all([
        refreshOrchardRegistry(created.data.id),
        refreshPortfolioAnalytics(),
      ])

      startTransition(() => {
        setCreateForm(INITIAL_CREATE_FORM)
        setSubmitNotice(`Created orchard ${created.data.name}.`)
        setIsSubmitting(false)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to create orchard."
        )
        setIsSubmitting(false)
      })
    }
  }

  async function handleCreateBlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrchardId) {
      setErrorMessage("Select an orchard before creating a block.")
      return
    }

    const payload = buildBlockRequest(blockForm)
    if (!payload.name || !payload.code) {
      setErrorMessage("Block name and code are required.")
      return
    }

    startTransition(() => {
      setIsBlockSubmitting(true)
      setErrorMessage(null)
      setSubmitNotice(null)
    })

    try {
      const created = await apiClient.createOrchardBlock(selectedOrchardId, {
        ...payload,
        organizationId: selectedOrganizationId ?? null,
      })
      await Promise.all([
        refreshOrchardRegistry(),
        refreshPortfolioAnalytics(),
        refreshSelectedOrchard(),
        refreshSelectedAnalytics(),
      ])

      startTransition(() => {
        setBlockForm(INITIAL_BLOCK_FORM)
        setRowForm((current) => ({
          ...current,
          blockId: current.blockId || created.data.id,
        }))
        setTreeForm((current) => ({
          ...current,
          blockId: current.blockId || created.data.id,
          rowId: "",
        }))
        setSubmitNotice(`Created block ${created.data.name}.`)
        setIsBlockSubmitting(false)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to create orchard block."
        )
        setIsBlockSubmitting(false)
      })
    }
  }

  async function handleCreateRow(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrchardId) {
      setErrorMessage("Select an orchard before creating a row.")
      return
    }

    const payload = buildRowRequest(rowForm)
    if (!payload.blockId || !payload.code) {
      setErrorMessage("Row block and code are required.")
      return
    }

    startTransition(() => {
      setIsRowSubmitting(true)
      setErrorMessage(null)
      setSubmitNotice(null)
    })

    try {
      const created = await apiClient.createOrchardRow(selectedOrchardId, {
        ...payload,
        organizationId: selectedOrganizationId ?? null,
      })
      await Promise.all([
        refreshSelectedOrchard(),
        refreshSelectedAnalytics(),
        refreshPortfolioAnalytics(),
      ])

      startTransition(() => {
        setRowForm((current) => ({
          ...INITIAL_ROW_FORM,
          blockId: current.blockId,
        }))
        setTreeForm((current) => ({
          ...current,
          blockId: current.blockId || created.data.blockId,
          rowId: created.data.id,
        }))
        setSubmitNotice(`Created row ${created.data.code}.`)
        setIsRowSubmitting(false)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to create orchard row."
        )
        setIsRowSubmitting(false)
      })
    }
  }

  async function handleCreateTree(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedOrchardId) {
      setErrorMessage("Select an orchard before creating a tree.")
      return
    }

    const payload = buildTreeRequest(treeForm, selectedOrchard, selectedBlock, selectedRow)
    if (!payload.blockId || !payload.treeCode || !payload.qrToken) {
      setErrorMessage("Tree block, code, and QR token are required.")
      return
    }

    startTransition(() => {
      setIsTreeSubmitting(true)
      setErrorMessage(null)
      setSubmitNotice(null)
    })

    try {
      const created = await apiClient.createOrchardTree(selectedOrchardId, {
        ...payload,
        organizationId: selectedOrganizationId ?? null,
      })
      await Promise.all([
        refreshOrchardRegistry(),
        refreshPortfolioAnalytics(),
        refreshSelectedOrchard(),
        refreshSelectedOrchardTrees(),
        refreshSelectedAnalytics(),
      ])

      startTransition(() => {
        setTreeForm((current) => ({
          ...INITIAL_TREE_FORM,
          blockId: current.blockId,
          rowId: current.rowId,
          status: current.status,
          variety: current.variety,
        }))
        setSubmitNotice(`Created tree ${created.data.treeCode}.`)
        setIsTreeSubmitting(false)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to create orchard tree."
        )
        setIsTreeSubmitting(false)
      })
    }
  }

  async function handleTreeImportFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    try {
      const nextCsvText = await file.text()
      startTransition(() => {
        setTreeImportCsvText(nextCsvText)
        setTreeImportSummary(null)
        setErrorMessage(null)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to read the selected CSV file."
        )
      })
    } finally {
      event.target.value = ""
    }
  }

  async function runTreeImport(mode: "dry_run" | "import") {
    if (!selectedOrchardId) {
      setErrorMessage("Select an orchard before importing trees.")
      return
    }

    const csv = treeImportCsvText.trim()

    if (!csv) {
      setErrorMessage("Paste CSV content or choose a CSV file before importing.")
      return
    }

    startTransition(() => {
      setIsTreeImporting(true)
      setTreeImportMode(mode)
      setErrorMessage(null)
      setSubmitNotice(null)
      setTreeImportSummary(null)
    })

    try {
      const result = await apiClient.importOrchardTrees(selectedOrchardId, {
        csv,
        dryRun: mode === "dry_run",
        organizationId: selectedOrganizationId ?? null,
      })

      if (mode === "import") {
        await Promise.all([
          refreshOrchardRegistry(),
          refreshPortfolioAnalytics(),
          refreshSelectedOrchard(),
          refreshSelectedOrchardTrees(),
          refreshSelectedAnalytics(),
        ])
      }

      startTransition(() => {
        setTreeImportSummary(result.data)
        setSubmitNotice(
          mode === "dry_run"
            ? `Validated CSV: ${result.data.created} rows ready and ${result.data.failed} failed.`
            : `Imported ${result.data.created} trees from CSV with ${result.data.failed} failed rows.`
        )
        setIsTreeImporting(false)
        setTreeImportMode(null)
      })
    } catch (error) {
      startTransition(() => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to import orchard trees from CSV."
        )
        setIsTreeImporting(false)
        setTreeImportMode(null)
      })
    }
  }

  async function handleImportTrees(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    await runTreeImport("import")
  }

  function handlePrintQrBatch() {
    if (!visibleQrExportTrees.length || typeof window === "undefined") {
      return
    }

    window.print()
  }

  return (
    <div className="orchard-workspace-root">
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }

          .orchard-workspace-root,
          .orchard-workspace-root * {
            visibility: visible !important;
          }

          body {
            background: #ffffff !important;
          }

          .orchard-workspace-root {
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0.18in !important;
            background: #ffffff !important;
          }

          .orchard-workspace-screen {
            display: none !important;
          }

          .orchard-print-sheet {
            display: block !important;
          }

          .orchard-print-toolbar {
            display: none !important;
          }

          .orchard-print-grid {
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(2.85in, 1fr)) !important;
            gap: 0.18in !important;
            align-items: start !important;
            margin: 0 !important;
          }

          .orchard-print-card {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            box-shadow: none !important;
            border-radius: 0.22in !important;
          }
        }
      `}</style>
      <div className="orchard-workspace-screen">
        <OperatorWorkspaceLayout
          badge="GrowBetter Orchard Intelligence"
          links={orchardLinks}
        >
      {errorMessage ? (
        <OperatorStatusMessage tone="danger">{errorMessage}</OperatorStatusMessage>
      ) : null}
      {submitNotice ? (
        <OperatorStatusMessage>{submitNotice}</OperatorStatusMessage>
      ) : null}

      <OperatorPanel
        title="Orchard portfolio"
        description="Create orchards, review orchard scope, and load orchard detail before tree-level QR and seasonal observation workflows are added."
      >
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-medium text-muted-foreground">Orchards</div>
            <div className="mt-2 text-2xl font-semibold">
              {isLoading ? "…" : formatNumber(metrics.orchards)}
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Orchards currently loaded for {selectedOrganizationLabel}.
            </p>
          </div>
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-medium text-muted-foreground">Blocks</div>
            <div className="mt-2 text-2xl font-semibold">
              {isLoading ? "…" : formatNumber(metrics.blocks)}
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Registered block-level groupings across the loaded orchards.
            </p>
          </div>
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-medium text-muted-foreground">Trees</div>
            <div className="mt-2 text-2xl font-semibold">
              {isLoading ? "…" : formatNumber(metrics.trees)}
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Tree registry count available for later QR and observation workflows.
            </p>
          </div>
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-medium text-muted-foreground">Observations</div>
            <div className="mt-2 text-2xl font-semibold">
              {isLoading ? "…" : formatNumber(metrics.observations)}
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Seasonal orchard records already captured in the selected scope.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="grid gap-1">
                <div className="text-sm font-medium">Comparative orchard view</div>
                <div className="text-sm leading-6 text-muted-foreground">
                  Cross-orchard comparison for {selectedOrganizationLabel} within {buildAnalyticsDateRangeLabel(search)}.
                </div>
              </div>
              {portfolioAnalytics ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPortfolioComparisonCsv}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download portfolio CSV
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPortfolioBenchmarksCsv}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download benchmark CSV
                  </Button>
                </div>
              ) : null}
            </div>

            {isPortfolioAnalyticsLoading ? (
              <div className="mt-4 grid gap-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={`orchard-portfolio-analytics-skeleton-${index}`}
                    className="h-24 w-full rounded-3xl"
                  />
                ))}
              </div>
            ) : portfolioAnalyticsError ? (
              <div className="mt-4">
                <OperatorStatusMessage tone="danger">
                  {portfolioAnalyticsError}
                </OperatorStatusMessage>
              </div>
            ) : portfolioAnalytics ? (
              <div className="mt-4 grid gap-4">
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Orchards with activity
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(portfolioAnalytics.orchardsWithActivityCount)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Observed trees
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(portfolioAnalytics.observedTreeCount)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Total observations
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(portfolioAnalytics.totalObservations)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Active trees
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(portfolioAnalytics.activeTreeCount)}
                    </div>
                  </div>
                </div>

                <div className="grid gap-3">
                  <div className="text-sm font-medium">Most active orchards</div>
                  {portfolioAnalytics.mostActiveOrchards.length ? (
                    portfolioAnalytics.mostActiveOrchards.map((orchard) => (
                      <div
                        key={orchard.orchardId}
                        className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="font-medium">{orchard.orchardName}</div>
                            <div className="text-sm text-muted-foreground">
                              {orchard.primaryVariety || "Variety pending"} · {formatNumber(orchard.treeCount)} trees · {formatPercentage(orchard.observationCoverageRatio)} coverage
                            </div>
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            <div>{formatNumber(orchard.totalObservations)} observations</div>
                            <div>{formatNumber(orchard.harvestEntries)} harvest entries</div>
                            <div>{formatDateTime(orchard.latestObservationAt)}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                      No cross-orchard activity recorded in the selected range.
                    </div>
                  )}
                </div>

                <div className="grid gap-3">
                  <div className="text-sm font-medium">Variety benchmarks</div>
                  {portfolioAnalytics.varietyBenchmarks.length ? (
                    <div className="grid gap-3">
                      {portfolioAnalytics.varietyBenchmarks.slice(0, 3).map((group) => (
                        <div
                          key={group.groupKey}
                          className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                        >
                          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="font-medium">{group.groupLabel}</div>
                              <div className="text-sm text-muted-foreground">
                                {formatNumber(group.orchardCount)} orchards · {formatPercentage(group.averageObservationCoverageRatio)} average coverage
                              </div>
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              {group.averageHarvestQuantityPerEntry !== null ? (
                                <div>
                                  Avg {group.averageHarvestQuantityPerEntry} {group.primaryHarvestUnit || "units"} per harvest entry
                                </div>
                              ) : (
                                <div>Yield baseline still forming</div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 grid gap-2">
                            {group.topOrchards.map((orchard) => (
                              <div
                                key={`${group.groupKey}-${orchard.orchardId}`}
                                className="rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm"
                              >
                                <div className="font-medium">{orchard.orchardName}</div>
                                <div className="text-muted-foreground">
                                  {formatNumber(orchard.totalObservations)} observations · {formatPercentage(orchard.observationCoverageRatio)} coverage
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                      Variety benchmarks will appear once the portfolio has orchard variety coverage.
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
            <div className="text-sm font-medium">Coverage watchlist</div>
            <div className="mt-1 text-sm leading-6 text-muted-foreground">
              Orchards below the observation coverage threshold for the current range.
            </div>

            {isPortfolioAnalyticsLoading ? (
              <div className="mt-4 grid gap-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={`orchard-coverage-skeleton-${index}`}
                    className="h-20 w-full rounded-3xl"
                  />
                ))}
              </div>
            ) : portfolioAnalytics?.lowCoverageOrchards.length ? (
              <div className="mt-4 grid gap-3">
                {portfolioAnalytics.lowCoverageOrchards.map((orchard) => (
                  <div
                    key={orchard.orchardId}
                    className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                  >
                    <div className="font-medium">{orchard.orchardName}</div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {formatPercentage(orchard.observationCoverageRatio)} coverage · {formatNumber(orchard.observedTreeCount)}/{formatNumber(orchard.treeCount)} trees observed
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {formatNumber(orchard.pendingObservationTreeCount)} trees still pending observation history.
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                No low-coverage orchards detected in the selected range.
              </div>
            )}

            {portfolioAnalytics ? (
              <div className="mt-6 grid gap-6">
                <div className="grid gap-3">
                  <div className="text-sm font-medium">Age-band benchmarks</div>
                  {portfolioAnalytics.ageBandBenchmarks.length ? (
                    portfolioAnalytics.ageBandBenchmarks.slice(0, 4).map((group) => (
                      <div
                        key={group.groupKey}
                        className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <div className="font-medium">{group.groupLabel}</div>
                            <div className="text-sm text-muted-foreground">
                              {formatNumber(group.orchardCount)} orchards · {formatPercentage(group.averageObservationCoverageRatio)} average coverage
                            </div>
                          </div>
                          <div className="text-right text-sm text-muted-foreground">
                            {group.averageHarvestQuantityPerEntry !== null
                              ? `${group.averageHarvestQuantityPerEntry} ${group.primaryHarvestUnit || "units"} avg`
                              : "Yield baseline pending"}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                      Age-band benchmarks will appear once orchards carry enough age data.
                    </div>
                  )}
                </div>

                <div className="grid gap-3">
                  <div className="text-sm font-medium">Yield pattern ranking</div>
                  {portfolioAnalytics.yieldPatternBenchmarks.length ? (
                    portfolioAnalytics.yieldPatternBenchmarks.map((group) => (
                      <div
                        key={group.groupKey}
                        className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                      >
                        <div className="font-medium">{group.groupLabel}</div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {formatNumber(group.orchardCount)} orchards in this pattern
                        </div>
                        <div className="mt-3 grid gap-2">
                          {group.topOrchards.map((orchard) => (
                            <div
                              key={`${group.groupKey}-${orchard.orchardId}`}
                              className="rounded-2xl border border-border/70 bg-background/70 px-3 py-3 text-sm"
                            >
                              <div className="font-medium">{orchard.orchardName}</div>
                              <div className="text-muted-foreground">
                                {orchard.averageHarvestQuantityPerEntry !== null
                                  ? `${orchard.averageHarvestQuantityPerEntry} ${orchard.primaryHarvestUnit || "units"} per harvest entry`
                                  : "No harvest baseline yet"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                      Yield pattern ranking will appear after harvest data is recorded across orchards.
                    </div>
                  )}
                </div>

                <div className="grid gap-3">
                  <div className="text-sm font-medium">Portfolio monthly activity</div>
                  {portfolioAnalytics.monthlyActivity.map((point) => {
                    const ratio = Math.max(
                      (point.observationCount / portfolioMonthlyActivityMax) * 100,
                      point.observationCount ? 10 : 0
                    )

                    return (
                      <div key={point.monthKey} className="grid gap-1">
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <span>{point.monthLabel}</span>
                          <span className="text-muted-foreground">
                            {formatNumber(point.observationCount)}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted/60">
                          <div
                            className="h-full rounded-full bg-emerald-500/70"
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </OperatorPanel>

      <OperatorPanel
        title="Observation analytics"
        description="Seasonal summaries derived from orchard observation history, including observation mix, harvest totals, and activity concentration by block."
      >
        {selectedOrchardId ? (
          <form
            className="mb-6 grid gap-3 rounded-3xl border border-border/70 bg-background/65 px-4 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
            onSubmit={handleApplyAnalyticsRange}
          >
            <label className="grid gap-2 text-sm font-medium">
              From
              <input
                type="date"
                className={INPUT_CLASS}
                value={search.analyticsFrom || ""}
                onChange={(event) => {
                  onSearchChange({
                    analyticsFrom: event.target.value || undefined,
                  })
                }}
              />
            </label>
            <label className="grid gap-2 text-sm font-medium">
              To
              <input
                type="date"
                className={INPUT_CLASS}
                value={search.analyticsTo || ""}
                onChange={(event) => {
                  onSearchChange({
                    analyticsTo: event.target.value || undefined,
                  })
                }}
              />
            </label>
            <Button type="submit" className="self-end">
              Apply range
            </Button>
            <Button
              type="button"
              variant="outline"
              className="self-end"
              onClick={handleResetAnalyticsRange}
            >
              Reset
            </Button>
          </form>
        ) : null}

        {!selectedOrchardId ? (
          <OperatorEmptyState
            title="Select an orchard first"
            message="Choose an orchard record to load analytics from its observation history."
          />
        ) : isAnalyticsLoading ? (
          <div className="grid gap-4 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`orchard-analytics-skeleton-${index}`}
                className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4"
              >
                <Skeleton className="h-4 w-28" />
                <Skeleton className="mt-3 h-8 w-16" />
                <Skeleton className="mt-3 h-4 w-full" />
              </div>
            ))}
          </div>
        ) : analyticsError ? (
          <OperatorStatusMessage tone="danger">
            {analyticsError}
          </OperatorStatusMessage>
        ) : selectedAnalytics ? (
          <div className="grid gap-6">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="grid gap-1">
                <div className="text-sm font-medium">Analytics range</div>
                <div className="text-sm text-muted-foreground">
                  {buildAnalyticsDateRangeLabel(search)}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleDownloadAnalyticsCsv}
              >
                <Download className="h-4 w-4" />
                Download CSV
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium text-muted-foreground">Observed trees</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(selectedAnalytics.observedTreeCount)}
                </div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {formatNumber(selectedAnalytics.pendingObservationTreeCount)} trees still have no recorded observation history.
                </p>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium text-muted-foreground">Total observations</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(selectedAnalytics.totalObservations)}
                </div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Latest activity {formatDateTime(selectedAnalytics.latestObservationAt)}.
                </p>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium text-muted-foreground">Harvest entries</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(selectedAnalytics.seasonalSummary.harvestEntries)}
                </div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Latest harvest {formatDateTime(selectedAnalytics.seasonalSummary.latestHarvestAt)}.
                </p>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium text-muted-foreground">Active trees</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(selectedAnalytics.activeTreeCount)}
                </div>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {formatNumber(selectedAnalytics.treeCount)} total registered trees in the orchard.
                </p>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Orchard insight summary</div>
                <div className="mt-4 grid gap-3">
                  {selectedAnalytics.managerSummary.length ? (
                    selectedAnalytics.managerSummary.map((insight, index) => (
                      <div
                        key={`orchard-insight-${index}`}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm leading-6 text-muted-foreground"
                      >
                        {insight}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      Orchard insights will appear once comparison data is available.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Seasonal comparison</div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Current window
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalComparison.current.totalObservations)}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {formatDateTime(selectedAnalytics.seasonalComparison.current.startAt)} to{" "}
                      {formatDateTime(selectedAnalytics.seasonalComparison.current.endAt)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Previous window
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalComparison.previous.totalObservations)}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {formatDateTime(selectedAnalytics.seasonalComparison.previous.startAt)} to{" "}
                      {formatDateTime(selectedAnalytics.seasonalComparison.previous.endAt)}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Observation delta
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatSignedDelta(selectedAnalytics.seasonalComparison.delta.totalObservations)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Harvest delta
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatSignedDelta(selectedAnalytics.seasonalComparison.delta.harvestEntries)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">No recent harvest</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(performanceFlags?.treesWithNoRecentHarvestCount)}
                </div>
                <div className="mt-4 grid gap-2">
                  {performanceFlags?.treesWithNoRecentHarvest.length ? (
                    performanceFlags.treesWithNoRecentHarvest.map((tree) => (
                      <div
                        key={tree.treeId}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                      >
                        <div className="font-medium">{tree.treeCode}</div>
                        <div className="text-muted-foreground">
                          {tree.blockCode ? `Block ${tree.blockCode}` : "Block pending"}
                          {tree.rowCode ? ` · Row ${tree.rowCode}` : ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      Every active tree has a harvest entry in the selected range.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Low observation coverage</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(performanceFlags?.blocksWithLowObservationCoverageCount)}
                </div>
                <div className="mt-4 grid gap-2">
                  {performanceFlags?.blocksWithLowObservationCoverage.length ? (
                    performanceFlags.blocksWithLowObservationCoverage.map((block) => (
                      <div
                        key={block.blockId}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                      >
                        <div className="font-medium">
                          {block.blockName || "Block"}
                          {block.blockCode ? ` · ${block.blockCode}` : ""}
                        </div>
                        <div className="text-muted-foreground">
                          {formatPercentage(block.coverageRatio)} coverage · {formatNumber(block.observedTreeCount)}/{formatNumber(block.totalTreeCount)} trees observed
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      No blocks are below the observation coverage threshold.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Inactive but still scanned</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(performanceFlags?.inactiveButStillScannedCount)}
                </div>
                <div className="mt-4 grid gap-2">
                  {performanceFlags?.inactiveButStillScanned.length ? (
                    performanceFlags.inactiveButStillScanned.map((tree) => (
                      <div
                        key={tree.treeId}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                      >
                        <div className="font-medium">{tree.treeCode}</div>
                        <div className="text-muted-foreground">
                          {tree.blockCode ? `Block ${tree.blockCode}` : "Block pending"}
                          {tree.rowCode ? ` · Row ${tree.rowCode}` : ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      No inactive trees were scanned in the selected range.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Low-performing trees</div>
                <div className="mt-2 text-2xl font-semibold">
                  {formatNumber(performanceFlags?.lowPerformingTreesCount)}
                </div>
                <div className="mt-4 grid gap-2">
                  {performanceFlags?.lowPerformingTrees.length ? (
                    performanceFlags.lowPerformingTrees.map((tree) => (
                      <div
                        key={tree.treeId}
                        className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                      >
                        <div className="font-medium">{tree.treeCode}</div>
                        <div className="text-muted-foreground">
                          {tree.averageHarvestQuantityPerEntry} {tree.primaryHarvestUnit || "units"} per entry
                        </div>
                        <div className="text-muted-foreground">
                          Baseline {tree.orchardBaselineQuantity} {tree.primaryHarvestUnit || "units"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      No low-performing trees are currently below the orchard harvest baseline.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Observation mix</div>
                <div className="mt-4 grid gap-3">
                  {observationTypeEntries.length ? (
                    (observationTypeEntries as Array<
                      [keyof OrchardAnalyticsRecord["observationTypeCounts"], number]
                    >).map(([type, count]) => {
                      const ratio = selectedAnalytics.totalObservations
                        ? Math.max((count / selectedAnalytics.totalObservations) * 100, 4)
                        : 0

                      return (
                        <div key={type} className="grid gap-1">
                          <div className="flex items-center justify-between gap-4 text-sm">
                            <span>{formatObservationTypeLabel(type)}</span>
                            <span className="text-muted-foreground">
                              {formatNumber(count)}
                            </span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-muted/60">
                            <div
                              className="h-full rounded-full bg-primary/70"
                              style={{ width: `${ratio}%` }}
                            />
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                      No observation types recorded in the selected range.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Seasonal summary</div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Flowering notes
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalSummary.floweringEntries)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Fruit set notes
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalSummary.fruitSetEntries)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Fruit load notes
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalSummary.fruitLoadEntries)}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-background px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Health checks
                    </div>
                    <div className="mt-2 text-lg font-semibold">
                      {formatNumber(selectedAnalytics.seasonalSummary.healthCheckEntries)}
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm leading-6 text-muted-foreground">
                  Latest harvest round{" "}
                  {selectedAnalytics.seasonalSummary.latestHarvestRound ?? "Not recorded"}.
                </div>
              </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Monthly activity</div>
                <div className="mt-4 grid gap-3">
                  {selectedAnalytics.monthlyActivity.map((point) => {
                    const ratio = Math.max(
                      (point.observationCount / monthlyActivityMax) * 100,
                      point.observationCount ? 10 : 0
                    )

                    return (
                      <div key={point.monthKey} className="grid gap-1">
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <span>{point.monthLabel}</span>
                          <span className="text-muted-foreground">
                            {formatNumber(point.observationCount)}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted/60">
                          <div
                            className="h-full rounded-full bg-emerald-500/70"
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm font-medium">Yield and block concentration</div>
                <div className="mt-4 grid gap-4">
                  <div className="grid gap-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Harvest totals
                    </div>
                    {selectedAnalytics.harvestYieldByUnit.length ? (
                      selectedAnalytics.harvestYieldByUnit.map((entry) => (
                        <div
                          key={entry.unit}
                          className="flex items-center justify-between rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                        >
                          <span>{entry.unit}</span>
                          <span className="font-medium">
                            {entry.totalQuantity} across {entry.entryCount} entries
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                        No harvest totals recorded yet.
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Most active blocks
                    </div>
                    {selectedAnalytics.blockActivity.length ? (
                      selectedAnalytics.blockActivity.map((block) => (
                        <div
                          key={block.blockId}
                          className="flex items-center justify-between rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm"
                        >
                          <span>
                            {block.blockName || "Block"}{block.blockCode ? ` · ${block.blockCode}` : ""}
                          </span>
                          <span className="font-medium">
                            {formatNumber(block.observationCount)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm text-muted-foreground">
                        No block activity recorded yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="text-sm font-medium">Block anomaly watchlist</div>
              <div className="mt-4 grid gap-3">
                {performanceFlags?.blockAnomalies.length ? (
                  performanceFlags.blockAnomalies.map((block) => (
                    <div
                      key={block.blockId}
                      className="rounded-2xl border border-border/70 bg-background px-4 py-4"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="font-medium">
                            {block.blockName || "Block"}{block.blockCode ? ` · ${block.blockCode}` : ""}
                          </div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            {block.anomalyReasons.map(formatBlockAnomalyReason).join(" · ")}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <div>{formatPercentage(block.coverageRatio)} coverage</div>
                          <div>{formatNumber(block.observationCount)} observations</div>
                          {block.averageHarvestQuantityPerEntry !== null && block.averageHarvestQuantityPerEntry !== undefined ? (
                            <div>
                              {block.averageHarvestQuantityPerEntry} {block.primaryHarvestUnit || "units"} avg
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-border/70 bg-background px-4 py-4 text-sm text-muted-foreground">
                    No block anomalies detected in the selected analytics range.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <OperatorEmptyState
            title="Analytics unavailable"
            message="Seasonal analytics will appear after an orchard is selected and the analytics request completes."
          />
        )}
      </OperatorPanel>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
        <OperatorPanel
          title="Create orchard"
          description="Register orchard identity first. Tree registry, QR export, and field observations build on this record."
        >
          <form className="grid gap-4" onSubmit={(event) => void handleCreateOrchard(event)}>
            {canManagePortalOrganizations(readySession) ? (
              <label className="grid gap-2 text-sm font-medium">
                Organization
                <select
                  className={INPUT_CLASS}
                  value={selectedOrganizationId || ""}
                  onChange={(event) => {
                    onSearchChange({
                      organizationId: event.target.value || undefined,
                      orchardId: undefined,
                    })
                  }}
                >
                  <option value="">All organizations</option>
                  {bundle?.organizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm font-medium">
                Orchard name
                <input
                  className={INPUT_CLASS}
                  value={createForm.name}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Crop type
                <input
                  className={INPUT_CLASS}
                  value={createForm.cropType}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      cropType: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Primary variety
                <input
                  className={INPUT_CLASS}
                  value={createForm.primaryVariety}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      primaryVariety: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Planting year
                <input
                  className={INPUT_CLASS}
                  inputMode="numeric"
                  value={createForm.plantingYear}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      plantingYear: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Area in acres
                <input
                  className={INPUT_CLASS}
                  inputMode="decimal"
                  value={createForm.areaAcres}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      areaAcres: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Owner name
                <input
                  className={INPUT_CLASS}
                  value={createForm.ownerName}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      ownerName: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Manager name
                <input
                  className={INPUT_CLASS}
                  value={createForm.managerName}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      managerName: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Manager phone
                <input
                  className={INPUT_CLASS}
                  value={createForm.managerPhone}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      managerPhone: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Village
                <input
                  className={INPUT_CLASS}
                  value={createForm.village}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      village: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                Taluk
                <input
                  className={INPUT_CLASS}
                  value={createForm.taluk}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      taluk: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                District
                <input
                  className={INPUT_CLASS}
                  value={createForm.district}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      district: event.target.value,
                    }))
                  }}
                />
              </label>
              <label className="grid gap-2 text-sm font-medium">
                State
                <input
                  className={INPUT_CLASS}
                  value={createForm.state}
                  onChange={(event) => {
                    setCreateForm((current) => ({
                      ...current,
                      state: event.target.value,
                    }))
                  }}
                />
              </label>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating orchard…" : "Create orchard"}
              </Button>
            </div>
          </form>
        </OperatorPanel>

        <OperatorPanel
          title="Orchard records"
          description="Load orchard detail before tree registry, QR export, and observation cycles are attached."
        >
          {isLoading ? (
            <p className="text-sm leading-6 text-muted-foreground">
              Loading orchard records…
            </p>
          ) : bundle?.orchards.length ? (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="grid gap-3">
                {bundle.orchards.map((orchard) => {
                  const isSelected = orchard.id === selectedOrchardId

                  return (
                    <button
                      key={orchard.id}
                      type="button"
                      onClick={() => {
                        onSearchChange({
                          orchardId: orchard.id,
                          section: "orchards",
                        })
                      }}
                      className={`rounded-3xl border px-4 py-4 text-left transition ${
                        isSelected
                          ? "border-primary/55 bg-primary/8"
                          : "border-border/70 bg-background/65 hover:border-primary/30"
                      }`}
                    >
                      <div className="text-sm font-semibold">{orchard.name}</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {orchard.cropType}
                        {orchard.primaryVariety ? ` · ${orchard.primaryVariety}` : ""}
                      </div>
                      <div className="mt-3 text-xs text-muted-foreground">
                        Trees {formatNumber(orchard._count?.trees ?? 0)} · Blocks{" "}
                        {formatNumber(orchard._count?.blocks ?? 0)}
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                {isDetailLoading ? (
                  <p className="text-sm leading-6 text-muted-foreground">
                    Loading orchard detail…
                  </p>
                ) : selectedOrchardError ? (
                  <OperatorStatusMessage tone="danger">
                    {selectedOrchardError}
                  </OperatorStatusMessage>
                ) : selectedOrchard ? (
                  <div className="grid gap-4">
                    <div>
                      <div className="text-xl font-semibold">
                        {selectedOrchard.name}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {selectedOrchard.cropType}
                        {selectedOrchard.primaryVariety
                          ? ` · ${selectedOrchard.primaryVariety}`
                          : ""}
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Location
                        </div>
                        <div className="mt-2 text-sm">
                          {[
                            selectedOrchard.village,
                            selectedOrchard.taluk,
                            selectedOrchard.district,
                            selectedOrchard.state,
                          ]
                            .filter(Boolean)
                            .join(", ") || "Not set"}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Area
                        </div>
                        <div className="mt-2 text-sm">
                          {selectedOrchard.areaAcres
                            ? `${selectedOrchard.areaAcres} acres`
                            : "Not set"}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Owner
                        </div>
                        <div className="mt-2 text-sm">
                          {selectedOrchard.ownerName || "Not set"}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Manager
                        </div>
                        <div className="mt-2 text-sm">
                          {selectedOrchard.managerName || "Not set"}
                          {selectedOrchard.managerPhone
                            ? ` · ${selectedOrchard.managerPhone}`
                            : ""}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Blocks
                        </div>
                        <div className="mt-2 text-lg font-semibold">
                          {formatNumber(selectedOrchard._count?.blocks ?? 0)}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Trees
                        </div>
                        <div className="mt-2 text-lg font-semibold">
                          {formatNumber(selectedOrchard._count?.trees ?? 0)}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-border/70 px-3 py-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          Observations
                        </div>
                        <div className="mt-2 text-lg font-semibold">
                          {formatNumber(selectedOrchard._count?.observations ?? 0)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium">Registered blocks</div>
                      {selectedOrchard.blocks?.length ? (
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {selectedOrchard.blocks.map((block) => (
                            <div
                              key={block.id}
                              className="rounded-2xl border border-border/70 px-3 py-3 text-sm"
                            >
                              <div className="font-medium">
                                {block.name} · {block.code}
                              </div>
                              <div className="mt-1 text-muted-foreground">
                                Rows {formatNumber(block._count?.rows ?? 0)} · Trees{" "}
                                {formatNumber(block._count?.trees ?? 0)}
                              </div>
                              {block.rows?.length ? (
                                <div className="mt-2 text-xs text-muted-foreground">
                                  Row codes: {block.rows.map((row) => row.code).join(", ")}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          No blocks registered yet.
                        </p>
                      )}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Last updated {formatDateTime(selectedOrchard.updatedAt)}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm leading-6 text-muted-foreground">
                    Select an orchard to load its detail.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <OperatorEmptyState
              title="No orchard records yet"
              message="Create the first orchard in this workspace. Tree registry, QR export, and field observations will attach to orchard records after this step."
            />
          )}
        </OperatorPanel>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
        <OperatorPanel
          title="Orchard structure"
          description="Define orchard blocks and rows before onboarding tree identities."
        >
          {!selectedOrchard ? (
            <OperatorEmptyState
              title="Select an orchard first"
              message="Choose an orchard record to register its blocks, rows, and tree structure."
            />
          ) : (
            <div className="grid gap-6">
              <form className="grid gap-4" onSubmit={(event) => void handleCreateBlock(event)}>
                <div className="text-sm font-medium">Create block</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2 text-sm font-medium">
                    Block name
                    <input
                      className={INPUT_CLASS}
                      value={blockForm.name}
                      onChange={(event) => {
                        setBlockForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Block code
                    <input
                      className={INPUT_CLASS}
                      value={blockForm.code}
                      onChange={(event) => {
                        setBlockForm((current) => ({
                          ...current,
                          code: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Sequence
                    <input
                      className={INPUT_CLASS}
                      inputMode="numeric"
                      value={blockForm.sequence}
                      onChange={(event) => {
                        setBlockForm((current) => ({
                          ...current,
                          sequence: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Variety override
                    <input
                      className={INPUT_CLASS}
                      value={blockForm.varietyOverride}
                      onChange={(event) => {
                        setBlockForm((current) => ({
                          ...current,
                          varietyOverride: event.target.value,
                        }))
                      }}
                    />
                  </label>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isBlockSubmitting}>
                    {isBlockSubmitting ? "Creating block…" : "Create block"}
                  </Button>
                </div>
              </form>

              <form className="grid gap-4" onSubmit={(event) => void handleCreateRow(event)}>
                <div className="text-sm font-medium">Create row</div>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="grid gap-2 text-sm font-medium">
                    Block
                    <select
                      className={INPUT_CLASS}
                      value={rowForm.blockId}
                      onChange={(event) => {
                        const blockId = event.target.value
                        setRowForm((current) => ({
                          ...current,
                          blockId,
                        }))
                      }}
                    >
                      <option value="">Select block</option>
                      {selectedOrchard.blocks?.map((block) => (
                        <option key={block.id} value={block.id}>
                          {block.name} · {block.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Row code
                    <input
                      className={INPUT_CLASS}
                      value={rowForm.code}
                      onChange={(event) => {
                        setRowForm((current) => ({
                          ...current,
                          code: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Sequence
                    <input
                      className={INPUT_CLASS}
                      inputMode="numeric"
                      value={rowForm.sequence}
                      onChange={(event) => {
                        setRowForm((current) => ({
                          ...current,
                          sequence: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Orientation
                    <input
                      className={INPUT_CLASS}
                      value={rowForm.orientation}
                      onChange={(event) => {
                        setRowForm((current) => ({
                          ...current,
                          orientation: event.target.value,
                        }))
                      }}
                    />
                  </label>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isRowSubmitting}>
                    {isRowSubmitting ? "Creating row…" : "Create row"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </OperatorPanel>

        <OperatorPanel
          title="Tree registry"
          description="Register tree identities and keep QR-ready tokens attached to orchard structure."
        >
          {!selectedOrchard ? (
            <OperatorEmptyState
              title="No orchard selected"
              message="Select an orchard before onboarding tree identities."
            />
          ) : (
            <div className="grid gap-6">
              <form className="grid gap-4" onSubmit={(event) => void handleCreateTree(event)}>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <label className="grid gap-2 text-sm font-medium">
                    Block
                    <select
                      className={INPUT_CLASS}
                      value={treeForm.blockId}
                      onChange={(event) => {
                        const blockId = event.target.value
                        setTreeForm((current) => ({
                          ...current,
                          blockId,
                          rowId: "",
                        }))
                      }}
                    >
                      <option value="">Select block</option>
                      {selectedOrchard.blocks?.map((block) => (
                        <option key={block.id} value={block.id}>
                          {block.name} · {block.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Row
                    <select
                      className={INPUT_CLASS}
                      value={treeForm.rowId}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          rowId: event.target.value,
                        }))
                      }}
                    >
                      <option value="">No row</option>
                      {availableRows.map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Tree code
                    <input
                      className={INPUT_CLASS}
                      value={treeForm.treeCode}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          treeCode: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Variety
                    <input
                      className={INPUT_CLASS}
                      value={treeForm.variety}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          variety: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Planting year
                    <input
                      className={INPUT_CLASS}
                      inputMode="numeric"
                      value={treeForm.plantingYear}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          plantingYear: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Age in years
                    <input
                      className={INPUT_CLASS}
                      inputMode="numeric"
                      value={treeForm.ageYears}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          ageYears: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium">
                    Status
                    <input
                      className={INPUT_CLASS}
                      value={treeForm.status}
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          status: event.target.value,
                        }))
                      }}
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-medium xl:col-span-2">
                    QR token
                    <input
                      className={INPUT_CLASS}
                      value={treeForm.qrToken}
                      placeholder={
                        buildSuggestedQrToken(
                          selectedOrchard,
                          selectedBlock,
                          selectedRow,
                          treeForm.treeCode
                        ) || "Auto-generated from orchard structure"
                      }
                      onChange={(event) => {
                        setTreeForm((current) => ({
                          ...current,
                          qrToken: event.target.value,
                        }))
                      }}
                    />
                  </label>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isTreeSubmitting}>
                    {isTreeSubmitting ? "Creating tree…" : "Create tree"}
                  </Button>
                </div>
              </form>

              <form
                className="grid gap-4 rounded-3xl border border-border/70 bg-background/50 px-4 py-4"
                onSubmit={(event) => void handleImportTrees(event)}
              >
                <div className="grid gap-1">
                  <div className="text-sm font-medium">Bulk tree import</div>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Upload or paste CSV for larger orchards. Blocks must already exist, and optional row codes must belong to the selected block.
                  </p>
                </div>

                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
                  <div className="grid gap-3">
                    <label className="grid gap-2 text-sm font-medium">
                      Orchard tree CSV
                      <textarea
                        className={`${INPUT_CLASS} min-h-48 resize-y font-mono text-xs leading-6`}
                        value={treeImportCsvText}
                        onChange={(event) => {
                          setTreeImportCsvText(event.target.value)
                        }}
                        placeholder={ORCHARD_TREE_IMPORT_TEMPLATE}
                      />
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="inline-flex cursor-pointer items-center rounded-2xl border border-border/70 bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/35">
                        Choose CSV file
                        <input
                          className="sr-only"
                          type="file"
                          accept=".csv,text/csv,text/plain"
                          onChange={(event) => {
                            void handleTreeImportFileChange(event)
                          }}
                        />
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setTreeImportCsvText(ORCHARD_TREE_IMPORT_TEMPLATE)
                          setTreeImportSummary(null)
                        }}
                      >
                        Load sample template
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm font-medium">CSV columns</div>
                    <div className="grid gap-2 text-sm leading-6 text-muted-foreground">
                      <div><span className="font-medium text-foreground">Required:</span> <code>block_code</code>, <code>tree_code</code></div>
                      <div><span className="font-medium text-foreground">Optional:</span> <code>row_code</code>, <code>variety</code>, <code>status</code>, <code>planting_year</code>, <code>age_years</code>, <code>qr_token</code></div>
                      <div>If <code>qr_token</code> is blank, the platform generates one from orchard, block, row, and tree code.</div>
                    </div>
                    {treeImportSummary ? (
                      <div className="grid gap-2 rounded-2xl border border-border/70 bg-background px-3 py-3 text-sm">
                        <div className="font-medium">
                          {treeImportSummary.dryRun ? "Last validation summary" : "Last import summary"}
                        </div>
                        <div className="text-muted-foreground">
                          {treeImportSummary.created} ready · {treeImportSummary.failed} failed · {treeImportSummary.totalRows} rows processed
                        </div>
                        {treeImportSummary.errors.length ? (
                          <>
                            <div className="grid gap-1 text-xs text-muted-foreground">
                              {treeImportSummary.errors.slice(0, 5).map((error) => (
                                <div key={`${error.rowNumber}-${error.message}`}>
                                  Row {error.rowNumber}: {error.message}
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-start">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={handleDownloadTreeImportErrorsCsv}
                              >
                                <Download className="h-4 w-4" />
                                Download failed rows
                              </Button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isTreeImporting}
                    onClick={() => {
                      void runTreeImport("dry_run")
                    }}
                  >
                    {isTreeImporting && treeImportMode === "dry_run"
                      ? "Validating CSV…"
                      : "Validate CSV first"}
                  </Button>
                  <Button type="submit" disabled={isTreeImporting}>
                    {isTreeImporting && treeImportMode === "import"
                      ? "Importing trees…"
                      : "Import CSV batch"}
                  </Button>
                </div>
              </form>

              {treeError ? (
                <OperatorStatusMessage tone="danger">{treeError}</OperatorStatusMessage>
              ) : null}

              {isTreeLoading ? (
                <p className="text-sm leading-6 text-muted-foreground">
                  Loading orchard trees…
                </p>
              ) : orchardTrees.length ? (
                <div className="grid gap-3">
                  {orchardTrees.map((tree) => (
                    <div
                      key={tree.id}
                      className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4"
                    >
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="font-medium">{tree.treeCode}</div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            {tree.block?.code || "No block"}
                            {tree.row?.code ? ` · Row ${tree.row.code}` : ""}
                            {tree.variety ? ` · ${tree.variety}` : ""}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Status {tree.status}
                        </div>
                      </div>
                      <div className="mt-3 grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
                        <div>QR token: {tree.qrToken}</div>
                        <div>
                          Last observation:{" "}
                          {tree.latestObservation?.observedAt
                            ? formatDateTime(tree.latestObservation.observedAt)
                            : "Not recorded"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <OperatorEmptyState
                  title="No trees registered yet"
                  message="Create the first tree identities for this orchard so QR export and field observations can start."
                />
              )}
            </div>
          )}
        </OperatorPanel>
      </div>

      <OperatorPanel
        title="QR export"
        description="Prepare printable tree tags directly from the orchard registry and reprint filtered batches by block when field labels are replaced."
      >
        {!selectedOrchard ? (
          <OperatorEmptyState
            title="No orchard selected"
            message="Select an orchard before preparing printable QR batches."
          />
        ) : orchardTrees.length ? (
          <div className="grid gap-6">
            <div className="orchard-print-toolbar flex flex-col gap-4 rounded-3xl border border-border/70 bg-background/65 px-4 py-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="grid gap-2">
                <div className="text-sm font-medium">Batch filters</div>
                <p className="text-sm leading-6 text-muted-foreground">
                  Filter the visible tag sheet by block, then print the current batch for field attachment or label replacement.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-[minmax(0,16rem)_auto] md:items-end">
                <label className="grid gap-2 text-sm font-medium">
                  Block filter
                  <select
                    className={INPUT_CLASS}
                    value={qrExportBlockId}
                    onChange={(event) => {
                      setQrExportBlockId(event.target.value)
                    }}
                  >
                    <option value="">All blocks</option>
                    {qrExportBlockOptions.map((block) => (
                      <option key={block.id} value={block.id}>
                        {block.name} · {block.code}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  type="button"
                  disabled={!visibleQrExportTrees.length}
                  onClick={handlePrintQrBatch}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print visible batch
                </Button>
              </div>
            </div>

            <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <div className="text-sm font-medium">Visible QR tags</div>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {visibleQrExportTrees.length} tree tags ready for {selectedOrchard.name}.
                  </p>
                </div>
                <div className="text-xs text-muted-foreground">
                  Scan destination: /orchard/tree/&lt;token&gt;
                </div>
              </div>

              <div
                data-testid="orchard-qr-export-grid"
                className="orchard-print-grid mt-4 grid gap-4 lg:grid-cols-3"
              >
                {visibleQrExportTrees.map((tree) => (
                  <div key={tree.id} data-testid={`orchard-qr-export-card-${tree.id}`}>
                    <OrchardQrExportCard
                      orchardName={selectedOrchard.name}
                      primaryVariety={selectedOrchard.primaryVariety}
                      tree={tree}
                    />
                  </div>
                ))} 
              </div>
            </div>
          </div>
        ) : (
          <OperatorEmptyState
            title="No tree registry available"
            message="Create tree identities first so printable orchard QR batches can be generated."
          />
        )}
      </OperatorPanel>
        </OperatorWorkspaceLayout>
      </div>

      {selectedOrchard && visibleQrExportTrees.length ? (
        <div className="orchard-print-sheet hidden">
          <div className="mb-4">
            <div className="text-lg font-semibold text-slate-950">
              {selectedOrchard.name} tree QR batch
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {visibleQrExportTrees.length} visible tags
              {qrExportBlockId
                ? ` · Block ${
                    qrExportBlockOptions.find((block) => block.id === qrExportBlockId)?.code
                    || "selected"
                  }`
                : ""}
            </div>
          </div>
          <div className="orchard-print-grid">
            {visibleQrExportTrees.map((tree) => (
              <OrchardQrExportCard
                key={`print-${tree.id}`}
                orchardName={selectedOrchard.name}
                primaryVariety={selectedOrchard.primaryVariety}
                tree={tree}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
